import CheckBoxIcon from "@mui/icons-material/CheckBox";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import { Autocomplete, Checkbox, FormHelperText } from "@mui/material";
import React from "react";

import { MultiSelectMessages } from "@/messages/inputs/multi-select.messages";
import { SelectData } from "@/types/form.types";

import MDInput from "../MDInput";
import { CHECKBOX_MARGIN_RIGHT, LISTBOX_MAX_HEIGHT, LISTBOX_OVERFLOW } from "./constants";

type Props<T = string> = {
  options: SelectData<T>[];
  label: string;
  value: T[];
  onChange: (value: T[]) => void;
  limit?: number;
  error?: string[] | string;
  messages?: MultiSelectMessages;
  disabled?: boolean;
  placeholder?: string;
};

function MultiSelect<T>({
  options,
  label,
  value,
  onChange,
  limit,
  error,
  disabled,
  placeholder,
}: Props<T>) {
  const handleChange = (event: React.SyntheticEvent, selectedOptions: SelectData<T>[]) => {
    const values = selectedOptions.map((option) => option.value);
    const isOnLimit = limit && values.length > limit;
    if (isOnLimit) return;

    onChange(values);
  };

  // Preserve selection/registration order (value[]), not options list order.
  const selectedOptions = value
    .map((selectedValue) => options.find((option) => option.value === selectedValue))
    .filter((option): option is SelectData<T> => option != null);

  return (
    <div>
      <Autocomplete
        multiple
        options={options}
        value={selectedOptions}
        disableCloseOnSelect
        getOptionLabel={(option) => option.label}
        isOptionEqualToValue={(option, value) => option.value === value.value}
        onChange={handleChange}
        ListboxProps={{
          style: {
            maxHeight: LISTBOX_MAX_HEIGHT,
            overflow: LISTBOX_OVERFLOW,
          },
        }}
        renderOption={(props, option, { selected }) => {
          const { key, ...rest } = props as unknown as { key: React.Key } & Record<string, unknown>;
          return (
            <li key={key} {...(rest as Record<string, unknown>)}>
              <Checkbox
                icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                checkedIcon={<CheckBoxIcon fontSize="small" />}
                style={{ marginRight: CHECKBOX_MARGIN_RIGHT }}
                checked={selected}
              />
              {option.label}
            </li>
          );
        }}
        renderInput={(params) => (
          <MDInput
            {...params}
            variant="standard"
            label={label}
            placeholder={placeholder}
            error={!!error}
            helperText={error}
            disabled={disabled}
          />
        )}
      />
      {error && <FormHelperText error>{error}</FormHelperText>}
    </div>
  );
}

export default MultiSelect;
